import React, { useMemo } from 'react';

import { SearchSelect } from '@/design-system/search-select';

import { warehouseApi } from '../api/warehouse.api';
import { useActivateOnFocus } from '../hooks/useActivateOnFocus';
import { type PageRequest, usePagedPicker } from '../hooks/usePagedPicker';
import type {
  GoodsIssueSource,
  GoodsIssueSourceStorage,
  Paginated,
} from '../types';
import { type PickerA11yProps, usePickerA11y } from './pickerA11y';
import { batchLabel, storageLabel } from './pickingEdit';

/**
 * The two server-searched pickers of the picking editor — Item location and
 * Batch number — over what a goods issue can reserve a SKU from. Like the
 * other server pickers they ask nothing until the user reaches them, then
 * page the first answer, debounce a typed search (the stale request aborted)
 * and load the next page when the list is scrolled to its end. `version`
 * starts the lists over (stock moved: a save was refused for it).
 */

interface SourceSelectProps extends PickerA11yProps {
  goodsIssueID: string;
  /** The row's SKU: nothing to list without one. */
  sku: string;
  /** Chosen id ('' = none). */
  value: string;
  /** What the chosen id is called while it is not in the loaded page. */
  selectedLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  version?: number;
}

const storageKeyOf = (s: GoodsIssueSourceStorage) => s.storageID;
const stockKeyOf = (c: GoodsIssueSource) => c.stockID;

interface SourcePickerProps<T> extends PickerA11yProps {
  /** Everything that selects the list besides the search; a new one starts over. */
  scope: string;
  /** What the list needs (the SKU, the storage …) is chosen. */
  ready: boolean;
  keyOf: (row: T) => string;
  fetchPage: (req: PageRequest, signal: AbortSignal) => Promise<Paginated<T>>;
  optionOf: (row: T) => { value: string; label: string };
  /** What the rows are called in messages: "lokasi" / "batch". */
  noun: string;
  /** The empty list, before any search. */
  emptyHint: string;
  /** Chosen id ('' = none). */
  value: string;
  selectedLabel?: string;
  onPick: (row: T) => void;
  disabled?: boolean;
  invalid?: boolean;
  placeholder: string;
}

/** The server-searched, paged picker both source lists are made of. */
const SourcePicker = <T,>({
  scope,
  ready,
  keyOf,
  fetchPage,
  optionOf,
  noun,
  emptyHint,
  value,
  selectedLabel,
  onPick,
  disabled,
  invalid,
  placeholder,
  'aria-label': ariaLabel,
}: SourcePickerProps<T>) => {
  const ref = usePickerA11y(ariaLabel, invalid);
  const { active, onFocus } = useActivateOnFocus();
  const picker = usePagedPicker<T>({
    scope,
    enabled: active && !disabled && ready,
    keyOf,
    fetchPage,
  });
  const options = useMemo(
    () => picker.rows.map(optionOf),
    [picker.rows, optionOf]
  );
  return (
    <div ref={ref} className="min-w-0" onFocus={onFocus}>
      <SearchSelect
        options={options}
        value={value}
        selectedLabelFallback={selectedLabel}
        onChange={(id) => {
          const row = picker.rows.find((r) => keyOf(r) === id);
          if (!row) return;
          onPick(row);
          picker.setSearch('');
        }}
        onSearchChange={picker.setSearch}
        disableLocalFilter
        hasMore={picker.hasMore}
        onLoadMore={picker.loadMore}
        loading={picker.loading}
        placeholder={placeholder}
        allowClear={false}
        disabled={disabled}
        error={invalid}
        emptyMessage={
          picker.error
            ? `Gagal memuat ${noun}: ${picker.error.message}`
            : picker.searching
              ? `${noun.charAt(0).toUpperCase()}${noun.slice(1)} tidak ditemukan`
              : emptyHint
        }
      />
    </div>
  );
};

const storageOption = (s: GoodsIssueSourceStorage) => ({
  value: s.storageID,
  label: storageLabel(s.storageName, s.storagePath),
});

const batchOption = (c: GoodsIssueSource) => ({
  value: c.stockID,
  label: batchLabel(c.batchNumber, c.expiredDate, c.available, c.stockType),
});

/** Item location: the storages holding candidates for the SKU. */
export const SourceStorageSelect: React.FC<
  SourceSelectProps & { onPick: (storage: GoodsIssueSourceStorage) => void }
> = ({
  goodsIssueID,
  sku,
  placeholder = 'Pilih lokasi',
  version = 0,
  ...rest
}) => (
  <SourcePicker<GoodsIssueSourceStorage>
    {...rest}
    placeholder={placeholder}
    scope={`${goodsIssueID}|${sku}|${version}`}
    ready={!!sku}
    keyOf={storageKeyOf}
    fetchPage={({ search, page, limit }, signal) =>
      warehouseApi.listGoodsIssueSourceStorages(
        goodsIssueID,
        { sku, search, page, limit },
        { signal }
      )
    }
    optionOf={storageOption}
    noun="lokasi"
    emptyHint="Tidak ada stok available untuk SKU ini"
  />
);

/** Batch number: the stock rows of the SKU in the chosen storage. */
export const SourceBatchSelect: React.FC<
  SourceSelectProps & {
    /** The chosen storage: nothing to list without one. */
    storageID: string;
    onPick: (source: GoodsIssueSource) => void;
  }
> = ({
  goodsIssueID,
  sku,
  storageID,
  placeholder = 'Pilih batch',
  version = 0,
  ...rest
}) => (
  <SourcePicker<GoodsIssueSource>
    {...rest}
    placeholder={placeholder}
    scope={`${goodsIssueID}|${sku}|${storageID}|${version}`}
    ready={!!sku && !!storageID}
    keyOf={stockKeyOf}
    fetchPage={({ search, page, limit }, signal) =>
      warehouseApi.listGoodsIssueSources(
        goodsIssueID,
        { sku, storageID, search, page, limit },
        { signal }
      )
    }
    optionOf={batchOption}
    noun="batch"
    emptyHint="Belum ada batch di lokasi ini"
  />
);

/**
 * SKU of a line: one of the ORDERED SKUs — the order's own lines, a short
 * local list (not a catalog), so the kit filters it client-side.
 */
export const OrderedSkuSelect: React.FC<
  PickerA11yProps & {
    skus: readonly string[];
    value: string;
    onPick: (sku: string) => void;
    disabled?: boolean;
    invalid?: boolean;
    placeholder?: string;
  }
> = ({
  skus,
  value,
  onPick,
  disabled,
  invalid,
  placeholder = 'Pilih SKU',
  'aria-label': ariaLabel,
}) => {
  const ref = usePickerA11y(ariaLabel, invalid);
  const options = useMemo(
    () => skus.map((sku) => ({ value: sku, label: sku })),
    [skus]
  );
  return (
    <div ref={ref} className="min-w-0">
      <SearchSelect
        options={options}
        value={value}
        selectedLabelFallback={value || undefined}
        onChange={(sku) => {
          if (sku) onPick(sku);
        }}
        placeholder={placeholder}
        allowClear={false}
        disabled={disabled}
        error={invalid}
        emptyMessage="SKU tidak ada di order"
      />
    </div>
  );
};
